import { Category as CategoryInterface } from '../types/resources';

/**
 * Category model class with utility methods
 */
export class Category {
  public readonly id: string;
  public readonly name: string;
  public readonly slug: string;
  public readonly parent?: string;
  public readonly children: Category[];
  public readonly resourceCount: number;

  constructor(data: CategoryInterface) {
    this.id = data.id;
    this.name = data.name;
    this.slug = data.slug;
    this.parent = data.parent;
    this.children = data.children?.map(child => new Category(child)) || [];
    this.resourceCount = data.resourceCount || 0;
  }

  /**
   * Generate the URL for this category
   * Pattern: /resources/{category-slug}
   */
  getUrl(): string {
    return `/resources/${this.slug}`;
  }

  /**
   * Get the full category path including parent categories
   */
  getFullPath(): string {
    // This would need to be implemented with access to the full category tree
    // For now, return just the current category slug
    return this.slug;
  }

  /**
   * Check if this category has children
   */
  hasChildren(): boolean {
    return this.children.length > 0;
  }

  /**
   * Check if this category is a root category (no parent)
   */
  isRoot(): boolean {
    return !this.parent;
  }

  /**
   * Get all descendant categories (children and their children)
   */
  getAllDescendants(): Category[] {
    const descendants: Category[] = [];
    
    for (const child of this.children) {
      descendants.push(child);
      descendants.push(...child.getAllDescendants());
    }
    
    return descendants;
  }

  /**
   * Find a child category by slug
   */
  findChildBySlug(slug: string): Category | undefined {
    return this.children.find(child => child.slug === slug);
  }
}